import React, { useEffect, useRef, useState } from "react";
import { getStatusColor } from "../core/btw-status-colors";
import BtwStatusPill from "./btw-status-pill";

const BtwStatusMenu = ({
  current,
  options = [],
  onSelect,
  disabled = false,
  align = "left",
}) => {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const esc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    setPicked(null);
    setComment("");
  };

  const submit = async () => {
    if (!picked || saving) return;
    setSaving(true);
    try {
      await onSelect(picked, comment.trim());
      close();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`group inline-flex items-center gap-1.5 rounded-full border border-transparent transition hover:border-gray-300 hover:shadow-sm ${
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
        }`}
      >
        <BtwStatusPill status={current} size="lg" />
        <svg
          className="mr-1 h-3.5 w-3.5 text-gray-500"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open ? (
        <div
          className={`absolute z-30 mt-2 w-80 rounded-xl bg-white p-2 shadow-lg ring-1 ring-gray-200 ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          <div className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
            Wijzig status
          </div>
          <div className="max-h-72 overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500">
                Geen statussen beschikbaar.
              </div>
            ) : (
              options.map((opt) => {
                const isCurrent = current?._id === opt._id;
                const isPicked = picked?._id === opt._id;
                const c = getStatusColor(opt.color);
                return (
                  <button
                    key={opt._id}
                    type="button"
                    onClick={() => setPicked(opt)}
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition ${
                      isPicked
                        ? "bg-gray-100"
                        : isCurrent
                        ? "bg-gray-50"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${c.dot}`} />
                      <span className="text-gray-900">{opt.name}</span>
                      {opt.isTerminal ? (
                        <span className="text-[10px] uppercase tracking-wide text-gray-400">
                          eindstation
                        </span>
                      ) : null}
                    </span>
                    {isCurrent ? (
                      <span className="text-[11px] text-gray-400">huidig</span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>

          {picked && picked._id !== current?._id ? (
            <div className="mt-2 border-t border-gray-100 pt-2">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Optionele opmerking bij deze wijziging…"
                rows={2}
                className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
              />
              <div className="mt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={close}
                  className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={submit}
                  className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
                >
                  {saving ? "Opslaan…" : "Wijzig status"}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default BtwStatusMenu;
